<script setup lang="ts">
import { dashboardApiClient } from '@api-client/dashboard';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { onMounted, ref } from 'vue';
import { toast } from '@/ui-plus/sonner';
import type { ResType } from '@api-client/typeUtils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@lib/utils/formatCurrency';
import { formatDate } from '@lib/utils/formatDate';
import Badge from '@/components/ui/badge/Badge.vue';
import { Loader2, History, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-vue-next';

const historyApiPath = dashboardApiClient.website.partner_marketing.store_history[':websiteId']['$post'];
type PartnerEarningsHistoryResponse = ResType<typeof historyApiPath>["data"];
type PartnerEarningsHistoryItem = PartnerEarningsHistoryResponse["items"][0];

// Computed properties based on partner type
// const partnerConfig = computed(() => ({
//     support: {
//         title: 'Support Partner History',
//         description: 'Recent earnings as a support partner (limit 10 per page)',
//         badgeText: 'Support Fee',
//         feeField: 'netSupportFeeWithoutTax' as const
//     },
//     marketing: {
//         title: 'Marketing Partner History',
//         description: 'Recent earnings as a marketing partner (limit 10 per page)',
//         badgeText: 'Marketing Fee',
//         feeField: 'netMarketingFeeWithoutTax' as const
//     }
// })[props.partnerType]);

// History state
const historyItems = ref<PartnerEarningsHistoryItem[]>([]);
const isHistoryLoading = ref(false);
const hasNextPage = ref(false);
const currentCursor = ref<{ created_at_ts: number; id: string } | null>(null);
const cursors = ref<Array<{ created_at_ts: number; id: string } | null>>([null]);
const currentPageIndex = ref(0);

onMounted(() => {
    loadHistoryData();
});

async function loadHistoryData(cursor?: { created_at_ts: number; id: string } | null) {
    isHistoryLoading.value = true;
    try {
        const { data, error } = await (await dashboardApiClient.website.partner_marketing.store_history[':websiteId'].$post({
            param: {
                websiteId: currentWebsiteId.value || ''
            },
            json: {
                cursor: cursor || undefined,
                pageSize: 10,
            }
        })).json();

        if (error) {
            toast.error(`Failed to load marketing partner history: ` + error.message);
        } else {
            historyItems.value = data?.items || [];
            hasNextPage.value = data?.hasNextPage || false;
            currentCursor.value = data?.nextCursor || null;
        }
    } catch (error: any) {
        console.error(`Failed to load marketing partner history:`, error);
        toast.error(`Failed to load marketing partner history: ` + error.message);
    } finally {
        isHistoryLoading.value = false;
    }
}

function goToNextPage() {
    if (hasNextPage.value && currentCursor.value) {
        cursors.value.push(currentCursor.value);
        currentPageIndex.value++;
        loadHistoryData(currentCursor.value);
    }
}

function goToPrevPage() {
    if (currentPageIndex.value > 0) {
        cursors.value.pop();
        currentPageIndex.value--;
        const prevCursor = cursors.value[cursors.value.length - 1];
        loadHistoryData(prevCursor);
    }
}

function refreshHistory() {
    cursors.value = [null];
    currentPageIndex.value = 0;
    loadHistoryData();
}

</script>

<template>
    <Card>
        <CardHeader>
            <div class="flex items-center justify-between">
                <div>
                    <CardTitle class="flex items-center gap-2">
                        <History class="h-5 w-5" />
                        Marketing Partner History
                    </CardTitle>
                    <CardDescription>
                        Recent earnings as a marketing partner (limit 10 per page)
                    </CardDescription>
                </div>
                <Button @click="refreshHistory"
                        :disabled="isHistoryLoading"
                        variant="outline"
                        size="sm">
                    <RefreshCw :class="{ 'animate-spin': isHistoryLoading }"
                               class="h-4 w-4 mr-2" />
                    Refresh
                </Button>
            </div>
        </CardHeader>
        <CardContent>
            <!-- Pagination Controls -->
            <div class="flex items-center justify-between pt-4">
                <div class="text-sm text-muted-foreground">
                    Page {{ currentPageIndex + 1 }}
                </div>
                <div class="flex gap-2">
                    <Button @click="goToPrevPage"
                            :disabled="currentPageIndex === 0 || isHistoryLoading"
                            variant="outline"
                            size="sm">
                        <ChevronLeft class="h-4 w-4 mr-1" />
                        Previous
                    </Button>
                    <Button @click="goToNextPage"
                            :disabled="!hasNextPage || isHistoryLoading"
                            variant="outline"
                            size="sm">
                        Next
                        <ChevronRight class="h-4 w-4 ml-1" />
                    </Button>
                </div>
            </div>

            <!-- Loading State -->
            <div v-if="isHistoryLoading && historyItems.length === 0"
                 class="flex justify-center items-center py-8">
                <Loader2 class="h-6 w-6 animate-spin" />
                <span class="ml-2">Loading history...</span>
            </div>

            <!-- No Data State -->
            <div v-else-if="!historyItems || historyItems.length === 0"
                 class="text-center py-8">
                <History class="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p class="text-muted-foreground">No marketing partner earnings history found</p>
            </div>

            <!-- History Data -->
            <div v-else
                 class="space-y-3">
                <div v-for="item in historyItems"
                     :key="item.marketingWebsiteAtomicBalanceTransactionId"
                     class="flex items-center justify-between p-3 border rounded-lg">
                    <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                            <Badge v-if="item.type === 'BALANCE_PAYOUT_WITH_STRIPE_TRANSFER'"
                                   variant="outline"
                                   class="text-xs">
                                PAYOUT_WITH_STRIPE
                            </Badge>
                            <Badge v-else-if="item.type === 'BALANCE_PAYOUT_WITH_STRIPE_TRANSFER_FAILED'"
                                   variant="destructive"
                                   class="text-xs">
                                PAYOUT_WITH_STRIPE_FAILED
                            </Badge>
                            <Badge v-else
                                   variant="outline"
                                   class="text-xs">
                                Marketing Fee
                            </Badge>
                            <Badge v-if="!item.processedAt"
                                   variant="outline"
                                   class="text-xs"
                                   title="Waiting to process">
                                ⏳
                            </Badge>
                            <Badge v-if="item.processedAt"
                                   variant="secondary"
                                   class="text-xs">
                                Processed
                            </Badge>
                        </div>
                        <div class="text-sm">
                            {{ formatDate(item.created_at_ts, undefined, true) }}
                        </div>
                        <div v-if="item.shortOtp"
                             class="text-xs font-mono">
                            Order: #{{ item.shortOtp }}
                            <Badge v-if="item.type === 'ORDER_CANCELLED'"
                                   variant="destructive"
                                   class="text-xs">
                                Cancelled
                            </Badge>
                        </div>
                        <div v-if="item.storeOrderId"
                             class="text-xs text-muted-foreground">
                            {{ item.storeOrderId }}
                        </div>
                        <div v-if="item.storeName"
                             class="text-xs font-mono">
                            Store: {{ item.storeName }} ({{ item.storeId }})
                        </div>
                        <div v-if="item.availableOnTs && item.isCharged"
                             class="text-xs text-muted-foreground">
                            Available: {{ formatDate(item.availableOnTs, undefined, true) }}
                        </div>
                        <div v-if="item.type === 'STRIPE_ORDER_VERIFIED'"
                             class="text-xs text-muted-foreground">
                            <Badge variant="secondary">
                                Stripe Order Verified
                            </Badge>
                            <br>
                            When Stripe captures the payment, you’ll see an updated entry in this list
                        </div>
                    </div>
                    <div class="text-right space-y-4">
                        <div v-if="item.endingBalance !== undefined">
                            <div class="text-xs text-muted-foreground">Ending Balance</div>
                            <div class="font-semibold">
                                <Badge v-if="!item.isCharged"
                                       variant="destructive"
                                       class="text-xs">
                                    Unpaid
                                </Badge>
                                <div v-else>
                                    {{ item.processedAt ? formatCurrency(item.endingBalance || 0, item.currency || 'GBP') : 'incoming...' }}
                                </div>
                            </div>
                        </div>
                        <div>
                            <div class="text-xs text-muted-foreground">Net</div>
                            <div :class="[
                                ' font-semibold',
                                item.net > 0 ? 'text-green-600' :
                                    item.net < 0 ? 'text-red-600' : 'text-muted-foreground'
                            ]">
                                {{ formatCurrency(item.net || 0, item.currency || 'GBP') }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination Controls -->
                <div class="flex items-center justify-between pt-4">
                    <div class="text-sm text-muted-foreground">
                        Page {{ currentPageIndex + 1 }}
                    </div>
                    <div class="flex gap-2">
                        <Button @click="goToPrevPage"
                                :disabled="currentPageIndex === 0 || isHistoryLoading"
                                variant="outline"
                                size="sm">
                            <ChevronLeft class="h-4 w-4 mr-1" />
                            Previous
                        </Button>
                        <Button @click="goToNextPage"
                                :disabled="!hasNextPage || isHistoryLoading"
                                variant="outline"
                                size="sm">
                            Next
                            <ChevronRight class="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </div>

            </div>
        </CardContent>
    </Card>
</template>
